/**
 * Meldingsgeluiden — cache met een vervalladder die altijd ergens eindigt.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-27
 * Plan: §9.4
 * Taken: T-2.13, T-2.14
 *
 * ── DE UI BLOKKEERT NOOIT OP AUDIO ──────────────────────────────────────────
 *
 * Dat is de enige regel die er echt toe doet. Niets in dit bestand wordt geawait vóór de
 * melding op het scherm staat; `playFor()` is bewust `void`-vriendelijk en `resolve()`
 * wordt pas aangeroepen NADAT de dialoog gerenderd is. Een tablet op een trage 4G-verbinding
 * moet een brandmelding tonen, ook als het chime-bestand er drie seconden over doet — en
 * zeker als het er nooit komt.
 *
 * ── DE VERVALLADDER ─────────────────────────────────────────────────────────
 *
 *     1. Cache Storage        — `caro-sounds-v1`, sleutel `${key}-${checksum}`
 *     2. Netwerk              — 3 seconden, via `timeoutSignal()`
 *     3. Ingebouwd SOUND_MAP  — `public/sounds/*.mp3`, altijd aanwezig, ook offline
 *     4. Stil                 — en dat wordt GEMELD, niet weggemoffeld
 *
 * Stap 4 is geen mislukking van de melding: `soundPlayed: false` gaat mee in de ack, zodat
 * "het geluid speelde niet" een cijfer wordt in plaats van een klacht. `delivered` hangt er
 * nooit van af.
 *
 * ── ⚠ `timeoutSignal()`, NOOIT `AbortSignal.timeout()` ──────────────────────
 *
 * `AbortSignal.timeout` bestaat pas vanaf Chromium 103. Een deel van deze vloot draait 69
 * tot 80. De native variant gooit daar bij het OPBOUWEN van het verzoek, dus vóór het
 * netwerk ooit geraakt wordt — twee features zijn daar al op stukgelopen (de
 * connectiviteitsprobe meldde "internet down" terwijl SIP gewoon werkte, en de
 * deurbel-WebRTC deed het niet meer). `timeoutSignal()` in `utils/` heeft de terugval.
 *
 * ── DE CHECKSUM MAAKT INVALIDATIE GRATIS ────────────────────────────────────
 *
 * De cachesleutel is inhoudsgeadresseerd: `${key}-${checksum}`. Verandert het bestand, dan
 * verandert de sleutel en is de oude entry vanzelf een mis. Er is dus geen
 * invalidatiebericht nodig — en dat is precies waarom de server een STABIELE URL levert en
 * geen ondertekende: een ondertekende URL verloopt na een uur en zou een cachehit binnen
 * datzelfde uur in een 403 laten lopen (T-0.02).
 *
 * ── CACHE STORAGE KAN ONTBREKEN ─────────────────────────────────────────────
 *
 * Op een WebView zonder beveiligde context bestaat `caches` niet. Elke aanraking zit
 * daarom in een try en valt terug op "geen cache": trager, maar werkend.
 */

import { timeoutSignal } from '../utils/timeoutSignal';

const CACHE_NAME = 'caro-sounds-v1';
const NETWORK_TIMEOUT_MS = 3000;

/**
 * De geauthenticeerde manier om audiobytes te halen, aangereikt door de app.
 *
 * Deze module kent geen auth en geen axios — net als `NotificationStore`. Zonder deze
 * functie valt hij terug op een kale `fetch`, en die werkt alleen voor bestanden die
 * zonder token bereikbaar zijn (de ingebouwde geluiden).
 */
let authedFetch: ((url: string) => Promise<Blob | null>) | null = null;

export function setFetcher(fn: ((url: string) => Promise<Blob | null>) | null): void {
  authedFetch = fn;
}
/** Meer dan dit bewaren we niet; een chime is klein maar de vloot draait jaren door. */
const MAX_ENTRIES = 20;

/**
 * De ingebouwde geluiden. Zitten in de bundle, dus ook zonder netwerk beschikbaar — dit is
 * stap 3 van de ladder en tegelijk het enige dat een tablet op dag één heeft.
 */
export const SOUND_MAP: Record<string, string> = {
  ringtone: '/sounds/ringtone.mp3',
  alarm: '/sounds/alarm.mp3',
  notification: '/sounds/notification.mp3',
  chime: '/sounds/chime.mp3',
};

/**
 * Prioriteit → ingebouwd geluid, voor als het gekozen geluid onbereikbaar is.
 *
 * ⚠ Bewust NIET `alarm` voor `urgent`: dat bestand is de noodtoon van de alarmknop en die
 * betekenis mag niet vervagen doordat een deurcontact hem ook gebruikt. Een bewoner die
 * `alarm` hoort moet kunnen aannemen dat er een alarm is.
 */
const FALLBACK_FOR_PRIORITY: Record<string, string> = {
  urgent: SOUND_MAP.chime,
  high: SOUND_MAP.notification,
  normal: SOUND_MAP.notification,
  low: SOUND_MAP.notification,
};

export interface SoundSpec {
  key: string;
  url?: string;
  checksum?: string;
  repeat?: number;
}

function cacheKeyFor(spec: SoundSpec): string {
  // Zonder checksum is er geen inhoudsadres en dus geen veilige cache. Dan liever elke keer
  // het netwerk dan een bestand dat stiekem verouderd is.
  return spec.checksum ? `/caro-sound/${spec.key}-${spec.checksum}` : '';
}

async function openCache(): Promise<Cache | null> {
  try {
    if (typeof caches === 'undefined') return null;
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

/**
 * Een `blob:`-URL uit een Response, of `null`.
 *
 * We geven een object-URL terug en geen Response, omdat `new Audio(src)` het enige is dat
 * op deze WebViews betrouwbaar afspeelt — Web Audio is op een deel van de vloot geblokkeerd
 * terwijl `<audio>` het wél doet (de audioprobe meet precies dat verschil).
 */
async function toObjectUrl(res: Response | undefined): Promise<string | null> {
  try {
    if (!res || !res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

/** Object-URL's die we zelf gemaakt hebben, zodat ze weer vrijgegeven kunnen worden. */
const ownedObjectUrls = new Set<string>();

function own(url: string | null): string | null {
  if (url && url.indexOf('blob:') === 0) ownedObjectUrls.add(url);
  return url;
}

export function releaseObjectUrl(url: string | null | undefined): void {
  if (!url || !ownedObjectUrls.has(url)) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* al vrijgegeven */
  }
  ownedObjectUrls.delete(url);
}

/**
 * Zoek een afspeelbare bron voor dit geluid. Werpt nooit.
 *
 * ⚠ Roep dit NA het renderen aan, niet ervoor. Zie de kop.
 *
 * @returns een URL die `new Audio()` aankan, of `null` als er niets speelbaar is.
 */
export async function resolve(
  spec: SoundSpec | null | undefined,
  priority: string = 'normal',
): Promise<string | null> {
  const fallback = FALLBACK_FOR_PRIORITY[priority] || SOUND_MAP.notification;
  if (!spec || !spec.key) return fallback;

  // Stap 3 vooraf: een sleutel die ingebouwd is, hoeft nooit over het netwerk.
  if (!spec.url && SOUND_MAP[spec.key]) return SOUND_MAP[spec.key];

  const cacheKey = cacheKeyFor(spec);
  const cache = await openCache();

  // ── 1. Cache ─────────────────────────────────────────────────────────────
  if (cache && cacheKey) {
    try {
      const hit = await cache.match(cacheKey);
      const url = await toObjectUrl(hit);
      if (url) return own(url);
    } catch {
      /* een kapotte cache is geen reden om niets af te spelen */
    }
  }

  // ── 2. Netwerk ───────────────────────────────────────────────────────────
  //
  // ⚠ VIA DE GEAUTHENTICEERDE OPHALER, NIET VIA EEN KALE `fetch`.
  //
  // `/api/v1/resident/notification-sounds/:id/audio` zit achter `deviceTokenAuth`. Een
  // kale fetch stuurt geen toestel-token mee, kreeg dus 401, en dan viel de ladder door
  // naar het ingebouwde geluid -- dat er voor een geüpload bestand niet is. Uitkomst:
  // stilte, zonder één zichtbare fout. Gemeten 2026-09-08.
  if (spec.url) {
    try {
      if (authedFetch) {
        const blob = await authedFetch(spec.url);
        if (blob && blob.size > 0) {
          if (cache && cacheKey) {
            try {
              await cache.put(cacheKey, new Response(blob));
              void prune(cache);
            } catch {
              /* geen ruimte of geen quotum — afspelen kan nog steeds */
            }
          }
          return own(URL.createObjectURL(blob));
        }
      }
      const res = await fetch(spec.url, { signal: timeoutSignal(NETWORK_TIMEOUT_MS) });
      if (res && res.ok) {
        if (cache && cacheKey) {
          try {
            await cache.put(cacheKey, res.clone());
            void prune(cache);
          } catch {
            /* geen ruimte of geen quotum — afspelen kan nog steeds */
          }
        }
        const url = await toObjectUrl(res);
        if (url) return own(url);
      }
    } catch {
      /* timeout, offline, of 404 — de ladder gaat door */
    }
  }

  // ── 3. Ingebouwd ─────────────────────────────────────────────────────────
  if (SOUND_MAP[spec.key]) return SOUND_MAP[spec.key];
  return fallback;
}

/**
 * Houd de cache klein.
 *
 * FIFO op invoegvolgorde: `cache.keys()` levert ze in die volgorde, dus de oudste gaan
 * eruit. Geen LRU — dat zou per afspeelbeurt een schrijfactie kosten op een toestel waarvan
 * de opslag traag en de levensduur eindig is, voor een winst die bij twintig chimes
 * verwaarloosbaar is.
 */
async function prune(cache: Cache): Promise<void> {
  try {
    const keys = await cache.keys();
    if (keys.length <= MAX_ENTRIES) return;
    const excess = keys.slice(0, keys.length - MAX_ENTRIES);
    for (const k of excess) {
      // eslint-disable-next-line no-await-in-loop
      await cache.delete(k);
    }
  } catch {
    /* opruimen is onderhoud, geen functionaliteit */
  }
}

/**
 * Laad bij het opstarten voor wat deze unit de laatste tijd gebruikt heeft.
 *
 * Werpt nooit en wordt nooit geawait door de app: mislukt hij volledig, dan is het enige
 * gevolg dat de eerste melding zijn geluid over het netwerk haalt — precies wat stap 2 van
 * de ladder al doet.
 */
export async function preload(specs: SoundSpec[] | null | undefined): Promise<void> {
  if (!specs || !specs.length) return;
  const cache = await openCache();
  if (!cache) return;
  for (const spec of specs) {
    const cacheKey = cacheKeyFor(spec);
    if (!cacheKey || !spec.url) continue;
    try {
      // eslint-disable-next-line no-await-in-loop
      const existing = await cache.match(cacheKey);
      if (existing) continue;
      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(spec.url, { signal: timeoutSignal(NETWORK_TIMEOUT_MS) });
      // eslint-disable-next-line no-await-in-loop
      if (res && res.ok) await cache.put(cacheKey, res);
    } catch {
      /* voorladen is comfort; de ladder vangt het op */
    }
  }
  await prune(cache);
}

/** Alleen voor tests. */
export async function __clear(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME);
  } catch {
    /* niets te wissen */
  }
  ownedObjectUrls.forEach((u) => {
    try {
      URL.revokeObjectURL(u);
    } catch {
      /* al weg */
    }
  });
  ownedObjectUrls.clear();
}
